"use client";

import NextImage, { type ImageProps as NextImageProps } from "next/image";
import NextLink, { LinkProps as NextLinkProps } from "next/link";
import { useMemo } from "react";

import {
	BloomreachLeadCapture,
	useBloomreachBanner
} from "@/app/components/shared/BloomreachLeadCapture";
import { useConsultantStore, useSessionStore } from "@/app/store";
import { resolveImageProps } from "@/app/utilities/images";
import { transformJavaScriptLink } from "@/app/utilities/link";
import { Footer, type MobileDesktopVisibility } from "@/composites/navigation";
import { ImageAsset } from "@/types";

interface ParentLink {
	title: string;
	href: string;
}

interface Column {
	mobile_desktop_visibility?: MobileDesktopVisibility;
	headline_url: {
		title: string;
		href: string;
	};
	reference: {
		parent_link: ParentLink;
		mobile_desktop_visibility?: MobileDesktopVisibility;
	}[];
}

export interface FooterBlockProps {
	shop_navigation_section: {
		column_1: Column[];
		column_2: Column[];
		column_3: Column[];
		column_4: Column[];
	};
	footer_logo: {
		url: string;
	};
	footer_text: string;
	social_navigation_row: {
		title: string;
		parent_link: ParentLink;
		image_thumbnail: ImageAsset;
	}[];
	email_link: {
		header_text: string;
		enter_email_language: string;
		sign_up_url: {
			title: string;
			href: string;
		};
		cta_button_text: string;
	};
	footer_main_navigation: {
		column_1: Column[];
		column_2: Column[];
		column_3: Column[];
	};
	copyright: string;
	legal_navigation_row: {
		parent_link: ParentLink;
	}[];
}

/**
 * Footer block component
 */
export function FooterBlock(props: FooterBlockProps): JSX.Element {
	const consultantState = useConsultantStore(
		(state) => state.consultantState
	);
	const sessionState = useSessionStore((state) => state.sessionState);

	const leadCaptureParams = useMemo(() => {
		const isConnected = consultantState.status === "connected";
		const country = isConnected
			? consultantState.address.country
			: sessionState?.locale === "en-CA"
				? "CA"
				: "US";
		return {
			consultantId: isConnected ? consultantState.globalId : undefined,
			pwsLink: isConnected ? consultantState.pwsUrl : undefined,
			country
		};
	}, [consultantState, sessionState?.locale]);

	const banner = useBloomreachBanner(leadCaptureParams);

	return (
		<>
			<Footer<NextLinkProps, NextImageProps>
				shopNav={[
					props?.shop_navigation_section?.column_1?.map(
						(column: Column) => ({
							title: column.headline_url.href
								? {
										component: NextLink,
										href: column.headline_url.href,
										children: column.headline_url.title
									}
								: column.headline_url.title,
							links:
								column.reference.length > 0
									? column.reference.map((link) => ({
											component: NextLink,
											href: link.parent_link.href ?? "",
											children:
												link.parent_link.title ?? "",
											mobile_desktop_visibility:
												link.mobile_desktop_visibility
										}))
									: undefined,
							mobile_desktop_visibility:
								column.mobile_desktop_visibility
						})
					) ?? [],
					props?.shop_navigation_section?.column_2?.map(
						(column: Column) => ({
							title: column.headline_url.href
								? {
										component: NextLink,
										href: column.headline_url.href,
										children: column.headline_url.title,
										mobile_desktop_visibility:
											column.mobile_desktop_visibility
									}
								: column.headline_url.title,
							links:
								column.reference.length > 0
									? column.reference.map((link) => ({
											component: NextLink,
											href: link.parent_link.href ?? "",
											children:
												link.parent_link.title ?? ""
										}))
									: undefined,
							mobile_desktop_visibility:
								column.mobile_desktop_visibility
						})
					) ?? [],
					props?.shop_navigation_section?.column_3?.map(
						(column: Column) => ({
							title: column.headline_url.href
								? {
										component: NextLink,
										href: column.headline_url.href,
										children: column.headline_url.title,
										mobile_desktop_visibility:
											column.mobile_desktop_visibility
									}
								: column.headline_url.title,
							links:
								column.reference.length > 0
									? column.reference.map((link) => ({
											component: NextLink,
											href: link.parent_link.href ?? "",
											children:
												link.parent_link.title ?? ""
										}))
									: undefined,
							mobile_desktop_visibility:
								column.mobile_desktop_visibility
						})
					) ?? [],
					props?.shop_navigation_section?.column_4?.map(
						(column: Column) => ({
							title: column.headline_url.href
								? {
										component: NextLink,
										href: column.headline_url.href,
										children: column.headline_url.title,
										mobile_desktop_visibility:
											column.mobile_desktop_visibility
									}
								: column.headline_url.title,
							links:
								column.reference.length > 0
									? column.reference.map((link) => ({
											component: NextLink,
											href: link.parent_link.href ?? "",
											children:
												link.parent_link.title ?? ""
										}))
									: undefined,
							mobile_desktop_visibility:
								column.mobile_desktop_visibility
						})
					) ?? []
				]}
				copyright={props?.copyright ?? ""}
				footerText={props?.footer_text ?? ""}
				socialLinks={
					props?.social_navigation_row?.map((link) => ({
						link: {
							component: NextLink,
							href: link.parent_link.href ?? ""
						},
						icon: {
							...resolveImageProps(
								undefined,
								link.image_thumbnail
							),
							component: NextImage,
							width: 32,
							height: 32
						}
					})) ?? []
				}
				logo={{
					...resolveImageProps(),
					component: NextImage,
					src: props?.footer_logo?.url ?? "",
					alt: "Pampered Chef",
					width: 250,
					height: 54
				}}
				newsletter={{
					title: props?.email_link?.header_text ?? "",
					description: props?.email_link?.enter_email_language ?? "",
					cta: {
						component: NextLink,
						href: props?.email_link?.sign_up_url?.href ?? "",
						children: props?.email_link?.cta_button_text ?? ""
					},
					onCtaClick: banner.open
				}}
				mainNav={[
					...(props?.footer_main_navigation?.column_1?.map(
						(column: Column) => ({
							title: column?.headline_url?.title ?? "",
							links:
								column?.reference?.map((link) => ({
									component: NextLink,
									href: link?.parent_link?.href ?? "",
									children: link?.parent_link?.title ?? "",
									mobile_desktop_visibility:
										link.mobile_desktop_visibility
								})) ?? [],
							mobile_desktop_visibility:
								column.mobile_desktop_visibility
						})
					) ?? []),
					...(props?.footer_main_navigation?.column_2?.map(
						(column: Column) => ({
							title: column?.headline_url?.title ?? "",
							links:
								column?.reference?.map((link) => ({
									component: NextLink,
									href: link?.parent_link?.href ?? "",
									children: link?.parent_link?.title ?? "",
									mobile_desktop_visibility:
										link.mobile_desktop_visibility
								})) ?? [],
							mobile_desktop_visibility:
								column.mobile_desktop_visibility
						})
					) ?? []),
					...(props?.footer_main_navigation?.column_3?.map(
						(column: Column) => ({
							title: column?.headline_url?.title ?? "",
							links:
								column?.reference?.map((link) => ({
									component: NextLink,
									href: link?.parent_link?.href ?? "",
									children: link?.parent_link?.title ?? "",
									mobile_desktop_visibility:
										link.mobile_desktop_visibility
								})) ?? [],
							mobile_desktop_visibility:
								column.mobile_desktop_visibility
						})
					) ?? [])
				]}
				legalNav={props?.legal_navigation_row?.map(
					(link: { parent_link: ParentLink }) => {
						const baseLink = {
							component: NextLink,
							href: link?.parent_link?.href,
							children: link?.parent_link?.title
						};
						return transformJavaScriptLink(baseLink) ?? baseLink;
					}
				)}
			/>
			<BloomreachLeadCapture {...banner} />
		</>
	);
}
